import { useCallback, useEffect, useState } from 'react';

// Custom hook that merges and manages Payouts, Withdrawals, Orders ... (in the future batches and so on)
export function useMergedEntities<T>({
  data,
  shouldRefresh,
  uniqueKey
}: {
  data: T[];
  shouldRefresh: boolean;
  uniqueKey: string;
}) {
  const [dataInternal, setDataInternal] = useState<T[]>([]);

  // Function to update a specific element in the internal state
  const updateElement = useCallback((elementToUpdate: T) => {
    // Update the element by matching the uniqueKey and replacing the old one
    setDataInternal((prevData) => {
      return prevData.map((item) =>
        item[uniqueKey] === elementToUpdate[uniqueKey] ? elementToUpdate : item
      );
    });
  }, []);

  const removeElement = useCallback(
    (elementToRemove: T) => {
      setDataInternal((prevData) => {
        return prevData.filter(
          (item) => item[uniqueKey] !== elementToRemove[uniqueKey]
        );
      });
    },
    [uniqueKey]
  );

  const cleanInternalData = useCallback(() => {
    setDataInternal([]);
  }, []);

  useEffect(() => {
    if (data && data.length > 0 && shouldRefresh) {
      setDataInternal((prevData) => {
        // Find any new items in entityData that are not present in prevData
        const newItems = data.filter((newItem) =>
          prevData.every(
            (prevItem) => prevItem[uniqueKey] !== newItem[uniqueKey]
          )
        );

        // Combine the updated items and new items into the final array
        return [...prevData, ...newItems];
      });
    }
  }, [shouldRefresh, data]);

  return { dataInternal, updateElement, cleanInternalData, removeElement };
}
